import { ApiError } from "../lib/api";
import { listOutbox, putOutboxItem, removeOutboxItem, type OutboxInspection, type OutboxPhoto } from "./outbox";

export interface SyncDeps {
  /** Cria a vistoria no servidor (idempotente por `payload.clientRequestId`). */
  createInspection(payload: Record<string, unknown>): Promise<{ id: string; code?: string }>;
  uploadPhoto(inspectionId: string, photo: OutboxPhoto): Promise<void>;
  now(): number;
}

export interface SyncResult { sent: Array<{ id: string; code?: string }>; failed: number; reviewing: number; skipped: number }

const PERMANENT_STATUS = new Set([400, 403, 404, 409, 410, 422]);
const MAX_BACKOFF_MS = 5 * 60_000;

/** Erro de regra de negócio/permissão: repetir não adianta, o usuário precisa revisar. Rede e 5xx são transitórios. */
export function isPermanentError(error: unknown): boolean {
  return error instanceof ApiError && PERMANENT_STATUS.has(error.status);
}

export function backoffMs(attempts: number): number {
  return Math.min(MAX_BACKOFF_MS, 5_000 * 2 ** Math.max(0, attempts - 1));
}

export const errorText = (error: unknown) => error instanceof Error && error.message ? error.message : "Não foi possível enviar agora.";

async function fail(item: OutboxInspection, error: unknown, deps: SyncDeps, context = ""): Promise<"review" | "retry"> {
  const permanent = isPermanentError(error);
  const attempts = item.attempts + 1;
  await putOutboxItem({
    ...item,
    attempts,
    status: permanent ? "precisa_revisao" : "pendente",
    nextAttemptAt: permanent ? 0 : deps.now() + backoffMs(attempts),
    lastError: `${context}${errorText(error)}`
  });
  return permanent ? "review" : "retry";
}

async function sendOne(item: OutboxInspection, deps: SyncDeps): Promise<{ outcome: "sent" | "review" | "retry"; code?: string }> {
  let current: OutboxInspection = { ...item, status: "enviando" };
  await putOutboxItem(current);

  if (!current.serverId) {
    try {
      const created = await deps.createInspection(current.payload);
      current = { ...current, serverId: created.id, serverCode: created.code };
      await putOutboxItem(current);
    } catch (error) { return { outcome: await fail(current, error, deps) }; }
  }

  for (const photo of [...current.photos]) {
    try {
      await deps.uploadPhoto(current.serverId!, photo);
      current = { ...current, photos: current.photos.filter((candidate) => candidate.id !== photo.id) };
      await putOutboxItem(current);
    } catch (error) {
      return { outcome: await fail(current, error, deps, `A vistoria ${current.serverCode ?? ""} foi enviada, mas a foto “${photo.label}” não: `.replace("  ", " ")), code: current.serverCode };
    }
  }

  await removeOutboxItem(current.id);
  return { outcome: "sent", code: current.serverCode };
}

let running: Promise<SyncResult> | null = null;

async function withCrossTabLock(work: () => Promise<SyncResult>): Promise<SyncResult> {
  const locks = (typeof navigator !== "undefined" ? navigator.locks : undefined) as LockManager | undefined;
  if (!locks) return work();
  const skipped: SyncResult = { sent: [], failed: 0, reviewing: 0, skipped: 0 };
  return (await locks.request("gestao-veicular-outbox-sync", { ifAvailable: true }, async (lock) => (lock ? work() : skipped))) as SyncResult;
}

/**
 * Envia, em ordem, as vistorias pendentes do usuário. Só uma sincronização roda por vez (nesta aba e entre abas).
 * Itens em "precisa_revisao" ou ainda em espera (backoff) são pulados.
 */
export function syncOutbox(userId: string, deps: SyncDeps): Promise<SyncResult> {
  running ??= withCrossTabLock(async () => {
    const result: SyncResult = { sent: [], failed: 0, reviewing: 0, skipped: 0 };
    const items = await listOutbox(userId);
    for (const item of items) {
      if (item.status === "precisa_revisao" || item.nextAttemptAt > deps.now()) { result.skipped += 1; continue; }
      const { outcome, code } = await sendOne(item, deps);
      if (outcome === "sent") result.sent.push({ id: item.id, code });
      else if (outcome === "review") result.reviewing += 1;
      else { result.failed += 1; break; /* rede/servidor instável: não insiste nos demais agora */ }
    }
    return result;
  }).finally(() => { running = null; });
  return running;
}

/** Libera um item para nova tentativa imediata (botão "Tentar agora"). */
export async function retryNow(item: OutboxInspection): Promise<void> {
  await putOutboxItem({ ...item, status: "pendente", nextAttemptAt: 0 });
}
